import { useEffect, useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://192.20.192.87:7501/api";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Client-side mirror of backend Ghana-standard math (fallback when backend unreachable). */
export function clientQuote(input, rates) {
  const r = {
    VCPU: 40, RAM_GB: 30, NVME_50GB: 19, HDD_50GB: 10, IP_EACH: 15, WINDOWS_SURCHARGE: 0,
    ...(rates || {}),
  };
  const units50 = (gb) => (gb <= 0 ? 0 : Math.ceil(gb / 50));
  const qty = Math.max(1, parseInt(input.quantity) || 1);
  const months = input.billingCycle === "Quarterly" ? 3 : input.billingCycle === "Annually" ? 12 : 1;
  const ips = Math.max(0, parseInt(input.dedicatedIPs) || 0);
  const nvme = Math.max(0, parseInt(input.nvmeGb) || 0);
  const hdd = Math.max(0, parseInt(input.hddGb) || 0);
  const isCustom = !!input.isCustom;
  let specCost, storageCost, ipCost;
  if (isCustom) {
    const cpu = Math.max(0, parseInt(input.cpuCores) || 0);
    const ram = Math.max(0, parseInt(input.ramGb) || 0);
    const nvmeCost = units50(nvme) * r.NVME_50GB;
    const hddCost = units50(hdd) * r.HDD_50GB;
    const win = String(input.os || "").toLowerCase().includes("windows") ? r.WINDOWS_SURCHARGE : 0;
    storageCost = round2(nvmeCost + hddCost);
    specCost = round2(cpu * r.VCPU + ram * r.RAM_GB + storageCost + win);
    ipCost = round2(ips * r.IP_EACH);
  } else {
    const base = Number(input.basePrice) || 0;
    storageCost = round2(units50(nvme) * r.NVME_50GB + units50(hdd) * r.HDD_50GB);
    ipCost = round2(ips * r.IP_EACH);
    specCost = round2(base + storageCost);
  }
  const pricePerMonth = round2((specCost + ipCost) * qty);
  const subtotal = round2(pricePerMonth * months);
  const nhil = round2(subtotal * 0.025);
  const getfund = round2(subtotal * 0.025);
  const vat = round2((subtotal + nhil + getfund) * 0.15);
  const total = round2(subtotal + nhil + getfund + vat);
  return {
    pricePerMonth: pricePerMonth.toFixed(2),
    subtotal: subtotal.toFixed(2),
    nhil: nhil.toFixed(2),
    getfund: getfund.toFixed(2),
    vat: vat.toFixed(2),
    total: total.toFixed(2),
    billingMonths: months,
    _fallback: true,
  };
}

/** Debounced server quote with client fallback. Returns quote object or null. */
export function useQuote(input, { delay = 400 } = {}) {
  const [quote, setQuote] = useState(null);
  const [rates, setRates] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/pricing/rates`).then(
      (res) => {
        if (cancelled) return;
        const map = {};
        (res.data || []).forEach((row) => { if (row.code) map[row.code] = Number(row.unitPriceGhs) || 0; });
        setRates(map);
      },
      () => {}
    );
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_BASE_URL}/pricing/quote`, input, { timeout: 8000 });
        setQuote({
          pricePerMonth: Number(data.pricePerMonth).toFixed(2),
          subtotal: Number(data.subtotal).toFixed(2),
          nhil: Number(data.nhil).toFixed(2),
          getfund: Number(data.getfund).toFixed(2),
          vat: Number(data.vat).toFixed(2),
          total: Number(data.total).toFixed(2),
          billingMonths: data.billingMonths,
          breakdown: data.breakdown,
          _fallback: false,
        });
      } catch {
        setQuote(clientQuote(input, rates || undefined));
      }
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(input), delay]);

  return { quote, rates };
}

export default useQuote;
